import { Link } from 'react-router-dom';
import { serviceAreas } from '../../../mocks/services';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ServiceAreasSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-28 bg-white overflow-hidden" id="service-areas">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
            <div className="h-px w-8 bg-brand-amber" />
            Service Areas
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-4">
            Proudly Serving Greater Jacksonville
          </h2>
          <p className="text-brand-warm-gray text-lg max-w-2xl mx-auto">
            BBM Services provides chimney cleaning, air duct cleaning, and dryer vent cleaning throughout Jacksonville and all surrounding communities in Duval, St. Johns, Clay, and Nassau counties.
          </p>
        </div>

        {/* Two-column layout: map left, areas right */}
        <div className="flex flex-col lg:flex-row gap-10 items-stretch mb-10">
          {/* Map — large and prominent */}
          <div className="lg:w-[55%] flex flex-col">
            <div className="rounded-3xl overflow-hidden border-2 border-stone-100 flex-1 min-h-[440px] relative">
              <iframe
                title="BBM Services Service Area — Jacksonville FL"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d442972.1!2d-81.9554!3d30.3322!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b716f1ceafeb%3A0xc4cd7d3896fcc7e3!2sJacksonville%2C%20FL!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '440px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            {/* Map caption */}
            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { icon: 'ri-map-2-line', text: 'Duval County' },
                { icon: 'ri-map-2-line', text: 'St. Johns County' },
                { icon: 'ri-map-2-line', text: 'Clay County' },
                { icon: 'ri-map-2-line', text: 'Nassau County' },
              ].map((c) => (
                <div key={c.text} className="flex items-center gap-2 text-sm text-brand-warm-gray bg-stone-50 border border-stone-200 px-3 py-1.5 rounded-full">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${c.icon} text-brand-amber text-xs`} />
                  </div>
                  {c.text}
                </div>
              ))}
            </div>
          </div>

          {/* Area grid */}
          <div className="lg:w-[45%] flex flex-col justify-between">
            <div className="grid grid-cols-2 gap-2.5 mb-6">
              {serviceAreas.map((area) => (
                <Link
                  key={area}
                  to="/service-areas"
                  className="flex items-center gap-2.5 p-3.5 bg-stone-50 rounded-xl border border-stone-100 hover:border-brand-amber hover:bg-amber-50/60 transition-all cursor-pointer group"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 group-hover:bg-brand-amber transition-colors">
                    <i className="ri-map-pin-2-fill text-brand-amber text-xs group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-brand-dark text-sm font-medium leading-tight">{area}</span>
                </Link>
              ))}
            </div>

            <div className="bg-brand-dark rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-amber rounded-xl flex-shrink-0">
                  <i className="ri-phone-fill text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-lg mb-1">Don't see your area?</h3>
                  <p className="text-white/55 text-sm">We likely serve you too. Call or message us to confirm we cover your neighborhood.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+19045550191"
                  className="flex-1 bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3 px-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Call (904) 555-0191
                </a>
                <Link
                  to="/service-areas"
                  className="flex-1 border-2 border-white/20 hover:border-white/40 text-white font-semibold py-3 px-4 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Full Service Area Map
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
